import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '../../lib/api';
import {
  authorizeInstagram,
  authorizeMessenger,
  checkChannel,
  connectWhatsApp,
  createChannel,
  deleteChannel,
  inboundParseUrl,
  disconnectInstagram,
  disconnectMessenger,
  instagramAvailable,
  listChannels,
  messengerAvailable,
  rotateChannelSecret,
  updateChannel,
  updateWhatsApp,
  webhookUrl,
  type ChannelConnectionRow,
  type WhatsAppInput,
} from '../../lib/channels-api';
import { ChannelHealthPanel } from './ChannelHealthPanel';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';
import { SecretInput } from './SecretInput';

const INSTAGRAM_OUTCOMES: Record<string, string> = {
  'not-configured': 'Instagram is not configured on this server.',
  'encryption-unavailable': 'Set CREDENTIALS_ENC_KEY on the server first — the access token is stored encrypted.',
  'exchange-failed': 'Instagram rejected the authorization. Try connecting again.',
  'connection-not-found': 'That connection no longer exists.',
  'account-mismatch': 'That is a different Instagram account from the one being reconnected.',
  'account-in-use': 'That Instagram account is already connected to another site.',
  'site-not-found': 'That site no longer exists.',
  denied: 'Instagram access was not granted.',
  'bad-request': 'The authorization response was invalid.',
};

const MESSENGER_OUTCOMES: Record<string, string> = {
  'not-configured': 'Messenger is not configured on this server.',
  'encryption-unavailable': 'Set CREDENTIALS_ENC_KEY on the server first — Page tokens are stored encrypted.',
  'exchange-failed': 'Facebook rejected the authorization. Try connecting again.',
  'no-pages': 'No Facebook Page you can message from was shared. Pick at least one Page on the Facebook screen.',
  'connection-not-found': 'That connection no longer exists.',
  'page-not-granted': 'The Page being reconnected was not shared on the Facebook screen.',
  'page-in-use': 'That Facebook Page is already connected to another site.',
  'site-not-found': 'That site no longer exists.',
  denied: 'Facebook access was not granted.',
  'bad-request': 'The authorization response was invalid.',
};

/** Instagram and Messenger connect through OAuth, not a signing secret. */
const isOAuth = (c: ChannelConnectionRow) => c.channel === 'instagram' || c.channel === 'messenger';

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

const SIGNING_HELP = `x-openline-signature: t=<unix seconds>,v1=<hex HMAC-SHA256(secret, "<t>.<raw body>")>`;

// Channels beyond the website widget: the signed API channel (any system can
// post customer messages and receive replies), email (SendGrid Inbound Parse
// in, the install's SMTP server out), Instagram and Messenger (Meta OAuth), and
// WhatsApp (Meta Cloud API, credentials pasted from Meta).
export function ChannelsSection({ siteId }: { siteId: string }) {
  const [rows, setRows] = useState<ChannelConnectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<'api' | 'email'>('api');
  const [name, setName] = useState('');
  const [outboundUrl, setOutboundUrl] = useState('');
  const [address, setAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [allowUnverified, setAllowUnverified] = useState(false);
  const [busy, setBusy] = useState(false);
  // Shown once after create/rotate; cleared on the next action.
  const [revealed, setRevealed] = useState<{ channel: ChannelConnectionRow; secret: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [instagram, setInstagram] = useState<{ configured: boolean; encryption: boolean } | null>(null);
  const [messenger, setMessenger] = useState<{ configured: boolean; encryption: boolean } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Back from Instagram's or Facebook's consent screen: the server redirects
  // here with the outcome in ?instagram= / ?messenger=. Show it once, then
  // clean the URL.
  useEffect(() => {
    const ig = searchParams.get('instagram');
    const fb = searchParams.get('messenger');
    if (!ig && !fb) return;
    if (ig === 'ok') setNotice('Instagram connected.');
    else if (ig) setError(INSTAGRAM_OUTCOMES[ig] ?? 'Could not connect Instagram.');
    if (fb === 'ok') setNotice('Messenger connected.');
    else if (fb === 'partial') setNotice('Messenger connected. Some Pages were skipped because they are connected to another site.');
    else if (fb) setError(MESSENGER_OUTCOMES[fb] ?? 'Could not connect Messenger.');
    const next = new URLSearchParams(searchParams);
    next.delete('instagram');
    next.delete('messenger');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    instagramAvailable(siteId).then(setInstagram).catch(() => setInstagram(null));
    messengerAvailable(siteId).then(setMessenger).catch(() => setMessenger(null));
  }, [siteId]);

  // Full-page redirect: Instagram's consent screen can't be framed, and the
  // callback sends the browser back here.
  async function connectInstagram(connectionId?: string) {
    setBusy(true); setError(null); setNotice(null);
    try {
      window.location.assign(await authorizeInstagram(siteId, connectionId));
    } catch (err) {
      setError(explain(err, 'Could not start the Instagram connection.'));
      setBusy(false);
    }
  }

  async function connectMessenger(connectionId?: string) {
    setBusy(true); setError(null); setNotice(null);
    try {
      window.location.assign(await authorizeMessenger(siteId, connectionId));
    } catch (err) {
      setError(explain(err, 'Could not start the Messenger connection.'));
      setBusy(false);
    }
  }

  async function load() {
    setLoading(true);
    try {
      setRows(await listChannels(siteId));
      setError(null);
    } catch {
      setError('Could not load channels.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  function explain(err: unknown, fallback: string): string {
    if (isApiError(err) && err.body && typeof err.body === 'object') {
      const b = err.body as { error?: string; detail?: string };
      if (b.error === 'instagram-not-configured') return 'Instagram is not configured on this server (META_APP_ID, META_APP_SECRET, META_OAUTH_REDIRECT_URI).';
      if (b.error === 'messenger-not-configured') return 'Messenger is not configured on this server (MESSENGER_APP_ID, MESSENGER_APP_SECRET, MESSENGER_OAUTH_REDIRECT_URI).';
      if (b.error === 'encryption-unavailable') return 'Set CREDENTIALS_ENC_KEY on the server first — channel secrets are stored encrypted.';
      if (b.detail) return b.detail;
    }
    return fallback;
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const { channel, secret } = await createChannel(
        siteId,
        kind === 'email'
          ? {
              channel: 'email',
              name,
              address: address.trim(),
              ...(fromName.trim() ? { fromName: fromName.trim() } : {}),
              ...(allowUnverified ? { allowUnverifiedSenders: true } : {}),
            }
          : { channel: 'api', name, ...(outboundUrl.trim() ? { outboundUrl: outboundUrl.trim() } : {}) },
      );
      setRevealed({ channel, secret });
      setName(''); setOutboundUrl(''); setAddress(''); setFromName(''); setAllowUnverified(false);
      await load();
    } catch (err) {
      setError(explain(err, 'Could not create the channel.'));
    } finally {
      setBusy(false);
    }
  }

  async function act(fn: () => Promise<unknown>, done?: string) {
    setBusy(true); setError(null); setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      await load();
    } catch (err) {
      setError(explain(err, 'That didn’t work.'));
    } finally {
      setBusy(false);
    }
  }

  async function check(c: ChannelConnectionRow) {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { result } = await checkChannel(siteId, c.id);
      setNotice(result.ok ? `${c.name}: OK${result.detail ? ` (${result.detail})` : ''}` : `${c.name}: ${result.detail ?? 'failed'}`);
      await load();
    } catch (err) {
      setError(explain(err, 'Check failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function rotate(c: ChannelConnectionRow) {
    if (!(await confirmDialog({ title: `Rotate the secret for ${c.name}?`, message: 'The old one stops working immediately.', confirmLabel: 'Rotate secret' }))) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const { channel, secret } = await rotateChannelSecret(siteId, c.id);
      setRevealed({ channel, secret });
    } catch (err) {
      setError(explain(err, 'Could not rotate the secret.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Channels</p>
      <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)', margin: 0 }}>
        The website widget is always on. An <strong>API channel</strong> lets another system post
        customer messages into this site&apos;s inbox and receive replies. An <strong>email channel</strong> turns
        mail to your support address into conversations, and sends agent and AI replies back as email in the same
        thread. Customers are matched to existing contacts by email or phone, so their history follows them across
        channels. <strong>Instagram</strong>{' '}
        connects a business or creator account&apos;s direct messages through Meta sign-in, and{' '}
        <strong>Messenger</strong> connects the Facebook Pages you choose through Facebook sign-in.{' '}
        <strong>WhatsApp</strong> connects a business number through Meta&apos;s WhatsApp Cloud API.
      </p>
      {error && <p className={styles.error} role="alert">{error}</p>}
      {notice && <p className={styles.notice} role="status">{notice}</p>}

      {revealed && (
        <div className={styles.notice} role="status">
          {revealed.channel.channel === 'email' ? (
            <>
              <strong>Inbound Parse URL — copy it now, its secret won&apos;t be shown again:</strong>
              <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all' }}>
                {inboundParseUrl(revealed.channel, revealed.secret)}
              </pre>
              <span className={styles.hint}>
                Paste it as the Destination URL of a SendGrid Inbound Parse host (see “How to set up an email channel” below).
              </span>
            </>
          ) : (
            <>
              <strong>Signing secret — copy it now, it won&apos;t be shown again:</strong>
              <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', userSelect: 'all' }}>{revealed.secret}</pre>
            </>
          )}
          <button type="button" className={styles.buttonGhost} onClick={() => setRevealed(null)}>I&apos;ve stored it</button>
        </div>
      )}

      <div className={styles.form}>
        <span className={styles.label}>Instagram</span>
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.button}
            disabled={busy || !instagram?.configured || !instagram.encryption}
            onClick={() => void connectInstagram()}
          >
            Connect Instagram →
          </button>
        </div>
        {instagram && !instagram.configured && (
          <span className={styles.hint}>Not configured: the server needs META_APP_ID, META_APP_SECRET and META_OAUTH_REDIRECT_URI.</span>
        )}
        {instagram?.configured && !instagram.encryption && (
          <span className={styles.hint}>Set CREDENTIALS_ENC_KEY on the server first — the access token is stored encrypted.</span>
        )}
      </div>

      <div className={styles.form}>
        <span className={styles.label}>Messenger</span>
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.button}
            disabled={busy || !messenger?.configured || !messenger.encryption}
            onClick={() => void connectMessenger()}
          >
            Connect Messenger →
          </button>
        </div>
        <span className={styles.hint}>
          On the Facebook screen, tick the Pages to connect; each becomes its own channel. Replies must be sent within
          24 hours of the customer&apos;s last message.
        </span>
        {messenger && !messenger.configured && (
          <span className={styles.hint}>Not configured: the server needs MESSENGER_APP_ID, MESSENGER_APP_SECRET and MESSENGER_OAUTH_REDIRECT_URI.</span>
        )}
        {messenger?.configured && !messenger.encryption && (
          <span className={styles.hint}>Set CREDENTIALS_ENC_KEY on the server first — Page tokens are stored encrypted.</span>
        )}
      </div>

      <WhatsAppConnect
        siteId={siteId}
        busy={busy}
        onConnected={(c) => {
          setNotice(`WhatsApp ${c.whatsapp?.displayPhone ?? ''} connected. Finish in Meta: paste the webhook URL and verify token shown below.`);
          void load();
        }}
        onError={(m) => setError(m)}
        explain={explain}
      />

      <form className={styles.form} onSubmit={create}>
        <label className={styles.field}>
          <span className={styles.label}>Type</span>
          <select className={styles.select} value={kind} onChange={(e) => setKind(e.target.value as 'api' | 'email')}>
            <option value="api">API</option>
            <option value="email">Email</option>
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Name</span>
          <input className={styles.input} required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'email' ? 'Support inbox' : 'CRM inbox'} />
        </label>
        {kind === 'email' ? (
          <>
            <label className={styles.field}>
              <span className={styles.label}>Support address</span>
              <input className={styles.input} type="email" required maxLength={320} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="help@support.example.com" />
              <span className={styles.hint}>
                Customers write to this address and replies are sent from it, so the server&apos;s SMTP account must be
                allowed to send as it.
              </span>
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Sender name (optional)</span>
              <input className={styles.input} maxLength={100} value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Example Support" />
            </label>
            <label className={styles.field} style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--inic-space-2)' }}>
              <input type="checkbox" checked={allowUnverified} onChange={(e) => setAllowUnverified(e.target.checked)} />
              <span className={styles.label} style={{ textTransform: 'none' }}>
                Accept mail that fails SPF/DKIM (not recommended — anyone could write in as one of your customers)
              </span>
            </label>
          </>
        ) : (
          <label className={styles.field}>
            <span className={styles.label}>Reply URL (optional)</span>
            <input className={styles.input} type="url" value={outboundUrl} onChange={(e) => setOutboundUrl(e.target.value)} placeholder="https://crm.example.com/openline/replies" />
            <span className={styles.hint}>Agent and AI replies are POSTed here, signed with the same secret. Leave empty for inbound only.</span>
          </label>
        )}
        <div className={styles.formActions}>
          <button type="submit" className={styles.button} disabled={busy}>
            {kind === 'email' ? 'Add email channel →' : 'Add API channel →'}
          </button>
        </div>
      </form>

      <ChannelHealthPanel siteId={siteId} refreshKey={rows.map((r) => `${r.id}${r.updatedAt}`).join()} />

      {loading ? (
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>Loading…</p>
      ) : rows.length === 0 ? null : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>Name</th><th>Status</th><th>Webhook URL</th><th>Replies</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{c.name} <span className={styles.badgeMuted}>{c.channel}</span></td>
                  <td>
                    <span className={c.status === 'active' ? styles.badgeAccent : styles.badgeMuted} title={c.lastError ?? undefined}>
                      {c.status === 'error' && isOAuth(c) ? 'reconnect needed' : c.status}
                    </span>
                    {c.rateLimitedUntil && (
                      <div className={styles.hint}>
                        Rate-limited by the provider — sends resume {new Date(c.rateLimitedUntil).toLocaleTimeString()}
                      </div>
                    )}
                    {c.instagram && (
                      <div className={styles.hint}>
                        {c.instagram.username ? `@${c.instagram.username}` : ''}
                        {c.status !== 'disconnected' && ` · token until ${fmtDate(c.instagram.tokenExpiresAt)}`}
                      </div>
                    )}
                    {c.messenger?.pageName && <div className={styles.hint}>Page: {c.messenger.pageName}</div>}
                    {c.whatsapp && (
                      <div className={styles.hint}>
                        {c.whatsapp.displayPhone} {c.whatsapp.verifiedName ? `· ${c.whatsapp.verifiedName}` : ''}
                        <br />
                        Verify token: <span className={styles.mono} style={{ userSelect: 'all' }}>{c.whatsapp.verifyToken}</span>
                      </div>
                    )}
                  </td>
                  <td className={styles.mono} style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'all' }} title={webhookUrl(c)}>
                    {webhookUrl(c)}
                  </td>
                  <td className={styles.mono} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.address ?? c.outboundUrl ?? ''}>
                    {c.channel === 'email' ? (c.address ?? '—') : (c.outboundUrl ?? '—')}
                  </td>
                  <td style={{ display: 'flex', gap: 'var(--inic-space-2)', flexWrap: 'wrap' }}>
                    {isOAuth(c) ? (
                      <>
                        {c.status !== 'disconnected' && (
                          <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => void check(c)}>Check</button>
                        )}
                        <button
                          type="button"
                          className={styles.buttonGhost}
                          disabled={busy || !(c.channel === 'messenger' ? messenger : instagram)?.configured}
                          onClick={() => void (c.channel === 'messenger' ? connectMessenger(c.id) : connectInstagram(c.id))}
                        >
                          {c.status === 'active' ? 'Reconnect' : c.channel === 'messenger' ? 'Reconnect Page' : 'Reconnect account'}
                        </button>
                        {c.status !== 'disconnected' && (
                          <button
                            type="button"
                            className={styles.buttonGhost}
                            disabled={busy}
                            onClick={async () => {
                              if (await confirmDialog({ title: `Disconnect ${c.name}?`, message: "Messages stop arriving and replies can't be sent until you reconnect. Conversations are kept.", confirmLabel: 'Disconnect' })) {
                                void act(
                                  () => (c.channel === 'messenger' ? disconnectMessenger(siteId, c.id) : disconnectInstagram(siteId, c.id)),
                                  `${c.name} disconnected.`,
                                );
                              }
                            }}
                          >
                            Disconnect
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => void check(c)}>Check</button>
                        {c.channel === 'whatsapp' ? (
                          <button
                            type="button"
                            className={styles.buttonGhost}
                            disabled={busy}
                            onClick={() => {
                              const accessToken = prompt('New WhatsApp access token:')?.trim();
                              const appSecret = accessToken ? prompt('Meta app secret (32 characters):')?.trim() : undefined;
                              if (!accessToken || !appSecret || !c.whatsapp?.phoneNumberId) return;
                              void act(
                                () => updateWhatsApp(siteId, c.id, { name: c.name, phoneNumberId: c.whatsapp!.phoneNumberId!, accessToken, appSecret }),
                                `${c.name}: credentials updated.`,
                              );
                            }}
                          >
                            Update token
                          </button>
                        ) : (
                          <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => void rotate(c)}>Rotate secret</button>
                        )}
                        <button
                          type="button"
                          className={styles.buttonGhost}
                          disabled={busy}
                          onClick={() => void act(() => updateChannel(siteId, c.id, { status: c.status === 'disconnected' ? 'active' : 'disconnected' }))}
                        >
                          {c.status === 'disconnected' ? 'Turn on' : 'Turn off'}
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className={styles.buttonDanger}
                      disabled={busy}
                      onClick={async () => {
                        if (await confirmDialog({ title: `Delete ${c.name}?`, message: 'Its conversations stay, but replies to them can no longer be delivered.' })) {
                          void act(() => deleteChannel(siteId, c.id), `${c.name} deleted.`);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details style={{ marginTop: 'var(--inic-space-3)' }}>
        <summary className={styles.label} style={{ cursor: 'pointer' }}>How to send messages to an API channel</summary>
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>
          POST JSON to the webhook URL, signed with the channel secret. Requests older than 5 minutes are refused,
          and each <code>event_id</code> is processed once, so retries are safe.
        </p>
        <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap' }}>{`${SIGNING_HELP}

{ "event_id": "evt_123", "type": "message",
  "from": { "id": "cust-42", "name": "Ada", "email": "ada@example.com" },
  "text": "Where is my order?" }

{ "event_id": "evt_124", "type": "status",
  "message_id": "<id from a reply>", "status": "delivered" }`}</pre>
      </details>

      <details style={{ marginTop: 'var(--inic-space-3)' }}>
        <summary className={styles.label} style={{ cursor: 'pointer' }}>How to set up an email channel</summary>
        <ol style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)', paddingLeft: 'var(--inic-space-4)' }}>
          <li>
            Make sure the server&apos;s SMTP settings (<code>SMTP_*</code>) may send as the support address. With
            SendGrid&apos;s relay, verify the support address&apos;s domain (or the address itself) in SendGrid as a sender.
          </li>
          <li>
            Pick a receiving domain for Inbound Parse, ideally a subdomain such as <code>support.example.com</code>, and
            point its MX record at <code>mx.sendgrid.net</code> (priority 10).
          </li>
          <li>
            In SendGrid → Settings → Inbound Parse, add that domain with the Destination URL shown when you created the
            channel. Leave “POST the raw, full MIME message” <strong>unchecked</strong>.
          </li>
          <li>
            Use an address on that domain as the support address, or forward your existing support mailbox to one.
            Mail is matched to a conversation by its reply headers; a new email starts a new conversation.
          </li>
        </ol>
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>
          Auto-replies, bounces, mailing-list mail and mail failing SPF/DKIM are ignored. Attachments are not imported
          yet; the conversation notes how many were sent.
        </p>
      </details>
    </section>
  );
}

/** Connect a WhatsApp business number: the IDs and secrets come from the
 *  Meta app's WhatsApp → API setup page. Checked against Meta before saving. */
function WhatsAppConnect({
  siteId,
  busy,
  onConnected,
  onError,
  explain,
}: {
  siteId: string;
  busy: boolean;
  onConnected: (c: ChannelConnectionRow) => void;
  onError: (message: string) => void;
  explain: (err: unknown, fallback: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<WhatsAppInput>({ name: 'WhatsApp', phoneNumberId: '', accessToken: '', appSecret: '' });
  const set = (k: keyof WhatsAppInput, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const c = await connectWhatsApp(siteId, form);
      setForm({ name: 'WhatsApp', phoneNumberId: '', accessToken: '', appSecret: '' });
      setOpen(false);
      onConnected(c);
    } catch (err) {
      onError(explain(err, 'Could not connect WhatsApp. Check the phone number ID, token and app secret.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.form}>
      <span className={styles.label}>WhatsApp</span>
      {!open ? (
        <div className={styles.formActions}>
          <button type="button" className={styles.button} disabled={busy} onClick={() => setOpen(true)}>
            Connect WhatsApp →
          </button>
        </div>
      ) : (
        <form className={styles.form} onSubmit={submit} style={{ gridColumn: '1 / -1' }}>
          <span className={styles.hint} style={{ gridColumn: '1 / -1' }}>
            In your Meta app: <strong>WhatsApp → API setup</strong> shows the phone number ID; create a permanent token for a
            System User with <code>whatsapp_business_messaging</code>; the app secret is under <strong>App settings → Basic</strong>.
            After connecting, set the webhook (callback URL and verify token shown in the list below) and subscribe to the{' '}
            <code>messages</code> field. Replies must be sent within 24 hours of the customer&apos;s last message.
          </span>
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input className={styles.input} required maxLength={100} value={form.name} onChange={(e) => set('name', e.target.value)} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Phone number ID</span>
            <input className={styles.input} required inputMode="numeric" value={form.phoneNumberId} onChange={(e) => set('phoneNumberId', e.target.value.trim())} placeholder="106540352242922" />
          </label>
          <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
            <span className={styles.label}>Access token</span>
            <SecretInput required value={form.accessToken} onChange={(e) => set('accessToken', e.target.value.trim())} placeholder="EAAG…" />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>App secret</span>
            <SecretInput required value={form.appSecret} onChange={(e) => set('appSecret', e.target.value.trim())} placeholder="32 hex characters" />
          </label>
          <div className={styles.formActions}>
            <button type="submit" className={styles.button} disabled={saving}>
              {saving ? 'Checking with Meta…' : 'Connect →'}
            </button>
            <button type="button" className={styles.buttonGhost} disabled={saving} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
